// Reducers combine the accumulated items with each streamed batch. They must return a new array
// (never mutate `items`) because the result is stored in the TanStack Query cache.

export const SORT_ORDER = { ASC: 'asc', DESC: 'desc' };

// Default order for every streamed list: newest / highest first.
export const DEFAULT_SORT_ORDER = SORT_ORDER.DESC;

// Only an explicit ascending order sorts ascending; anything else (missing, unknown) is descending.
const isAscending = (order) => `${order ?? DEFAULT_SORT_ORDER}`.toLowerCase() === SORT_ORDER.ASC;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;
const isMissing = (value) => value === undefined || value === null || Number.isNaN(value);

/**
 * Normalises a field value for comparison: ISO date strings and `Date`s become epoch milliseconds,
 * numbers stay numbers, other strings are compared as strings.
 */
export const toSortableValue = (value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' && ISO_DATE_PATTERN.test(value)) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? value : time;
  }
  return value;
};

const compareValues = (a, b) => {
  if (typeof a === typeof b) return a < b ? -1 : a > b ? 1 : 0;
  // Mixed types: numbers before strings, so ordering is still deterministic.
  return typeof a === 'number' ? -1 : 1;
};

/**
 * Builds a comparator on one field. Each item's sort value is computed once and cached, and items
 * missing the field always sort last (whatever the order).
 * @param {{ sortBy?: string, order?: 'asc'|'desc', getSortValue?: (item: *) => * }} options
 *   `order` defaults to `DEFAULT_SORT_ORDER` ('desc'). `getSortValue` overrides reading
 *   `item[sortBy]` (e.g. for nested or derived values).
 */
export const createComparator = ({ sortBy, order = DEFAULT_SORT_ORDER, getSortValue } = {}) => {
  const readValue = getSortValue || ((item) => toSortableValue(item?.[sortBy]));
  const direction = isAscending(order) ? 1 : -1;
  const cache = new WeakMap();
  const valueOf = (item) => {
    if (!item || typeof item !== 'object') return readValue(item);
    let value = cache.get(item);
    if (value === undefined) {
      value = readValue(item) ?? null;
      cache.set(item, value);
    }
    return value;
  };

  return (a, b) => {
    const valueA = valueOf(a);
    const valueB = valueOf(b);
    const missingA = isMissing(valueA);
    const missingB = isMissing(valueB);
    if (missingA || missingB) return missingA === missingB ? 0 : missingA ? 1 : -1;
    return direction * compareValues(valueA, valueB);
  };
};

// Linear merge of two sorted arrays. On ties `existing` comes first, so equal items keep their
// arrival order (stable).
const mergeSorted = (existing, incoming, compare) => {
  if (!existing.length) return incoming;
  if (compare(existing[existing.length - 1], incoming[0]) <= 0) return existing.concat(incoming);
  if (compare(incoming[incoming.length - 1], existing[0]) < 0) return incoming.concat(existing);

  const merged = new Array(existing.length + incoming.length);
  let i = 0;
  let j = 0;
  let k = 0;
  while (i < existing.length && j < incoming.length) {
    merged[k++] = compare(incoming[j], existing[i]) < 0 ? incoming[j++] : existing[i++];
  }
  while (i < existing.length) merged[k++] = existing[i++];
  while (j < incoming.length) merged[k++] = incoming[j++];
  return merged;
};

export const appendBatch = (items = [], batch = []) =>
  batch?.length ? items.concat(batch) : items;

/**
 * Builds a reducer that keeps the streamed list de-duplicated and ordered as batches arrive.
 *
 * With `compare`, each batch is sorted on its own (O(m log m)) and merged into the already-sorted
 * list in one linear pass (O(n + m)), instead of re-sorting everything (O(n log n)) per batch.
 * Since every update must produce a new array, O(n + m) is the minimum cost per batch.
 *
 * With `getKey`, an item whose key was already received replaces the earlier one. The key index is
 * maintained incrementally, so it is not rebuilt on every batch. Items with a `null`/`undefined` key
 * are never de-duplicated.
 *
 * Assumes `items` is the array this reducer returned for the previous batch (or an empty/initial
 * value that already satisfies `compare`).
 *
 * @param {{ getKey?: (item: *) => *, compare?: (a: *, b: *) => number }} [options]
 */
export const createMergeReducer = ({ getKey, compare } = {}) => {
  // Accumulated array -> Set of its keys. Ownership moves to the newly returned array each batch.
  const keyIndex = new WeakMap();

  const takeKeys = (items) => {
    const owned = keyIndex.get(items);
    if (owned) {
      keyIndex.delete(items);
      return owned;
    }
    const keys = new Set();
    items.forEach((item) => {
      const key = getKey(item);
      if (!isMissing(key)) keys.add(key);
    });
    return keys;
  };

  return (items = [], batch = []) => {
    if (!batch?.length) return items;

    let existing = items;
    let incoming = batch;
    let keys = null;

    if (getKey) {
      keys = takeKeys(items);
      const latestByKey = new Map();
      batch.forEach((item) => {
        const key = getKey(item);
        if (!isMissing(key)) latestByKey.set(key, item);
      });
      // Keep the last occurrence of each key within the batch, in arrival order.
      incoming = batch.filter((item) => {
        const key = getKey(item);
        return isMissing(key) || latestByKey.get(key) === item;
      });

      const replacedKeys = [...latestByKey.keys()].filter((key) => keys.has(key));
      if (replacedKeys.length) {
        const replaced = new Set(replacedKeys);
        existing = items.filter((item) => !replaced.has(getKey(item)));
      }
      latestByKey.forEach((_, key) => keys.add(key));
    }

    const result = compare
      ? mergeSorted(existing, incoming.slice().sort(compare), compare)
      : existing.concat(incoming);

    if (keys && result.length) keyIndex.set(result, keys);
    return result;
  };
};

/**
 * Reducer that auto-sorts streamed items by a field as they arrive (see `createMergeReducer`).
 * `order` defaults to `DEFAULT_SORT_ORDER` ('desc').
 * @param {{ sortBy?: string, order?: 'asc'|'desc', getSortValue?: (item: *) => *, getKey?: (item: *) => * }} options
 * @example createSortedMergeReducer({ sortBy: 'utc_timestamp_inms', getKey: (e) => e.id })
 */
export const createSortedMergeReducer = ({
  sortBy,
  order = DEFAULT_SORT_ORDER,
  getSortValue,
  getKey,
} = {}) =>
  createMergeReducer({ getKey, compare: createComparator({ sortBy, order, getSortValue }) });
